<script setup>
/** A coloured monogram for the owner: the hue derives from the label, so the same
 *  person keeps the same colour on every screen. */
import { computed } from 'vue'

const props = defineProps({
  /** The sanitised owner label, e.g. `pbourhis`. Drives the colour. */
  ownerLabel: { type: String, default: '' },
  /** Display name, when known: gives better initials than the owner label. */
  name: { type: String, default: '' },
  size: { type: Number, default: 22 },
  /** Show the name next to the avatar. */
  withName: { type: Boolean, default: false },
})

const label = computed(() => props.name || props.ownerLabel || 'unknown')

const initials = computed(() => {
  const source = props.name || props.ownerLabel
  if (!source) return '?'
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return source.slice(0, 2).toUpperCase()
})

/** Stable hue per user: same person, same colour, on every screen. */
const hue = computed(() => {
  let hash = 0
  for (const character of props.ownerLabel || label.value) {
    hash = (hash * 31 + character.charCodeAt(0)) % 360
  }
  return hash
})
</script>

<template>
  <span class="owner" :title="label">
    <span
      class="owner__monogram"
      :style="{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${Math.round(size * 0.42)}px`,
        '--hue': hue,
      }"
      aria-hidden="true"
      >{{ initials }}</span
    >
    <span v-if="withName" class="owner__name">{{ label }}</span>
    <span v-else class="sr-only">{{ label }}</span>
  </span>
</template>

<style scoped>
.owner {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  min-width: 0;
}

.owner__monogram {
  flex: none;
  border-radius: var(--radius-pill);
  display: grid;
  place-items: center;
  background: hsl(var(--hue) 45% 92%);
  color: hsl(var(--hue) 45% 28%);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.02em;
  line-height: 1;
}

:root[data-theme='dark'] .owner__monogram {
  background: hsl(var(--hue) 30% 22%);
  color: hsl(var(--hue) 55% 78%);
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']) .owner__monogram {
    background: hsl(var(--hue) 30% 22%);
    color: hsl(var(--hue) 55% 78%);
  }
}

.owner__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
